import { useEffect, useMemo, useState } from 'react';
import { Form, Input, Modal, Select, message } from '@maxtropy/components';
import { Spin } from 'antd';
import {
  apiV2GasPreparationStationDeviceListPost,
  apiV2GasPreparationStationNitrogenGeneratorAddPost,
  apiV2GasPreparationStationNitrogenGeneratorEditPost,
  apiV2GasPreparationStationNitrogenGeneratorGetPost,
} from '@maxtropy/device-customer-apis-v2';
import { DeviceType, NodeType, OperationType } from '../../utils';
import { DeviceList, ModalProp } from '../../IntrasiteTopology';

const FormItem = Form.Item;

const NitrogenGeneratorNode = ({ onCancel, onOk, uetId, operationType, selectedNode, stationData }: ModalProp) => {
  const [form] = Form.useForm();
  const [electricityMeterList, setElectricityMeterList] = useState<DeviceList[]>([]);
  const [flowmeterList, setFlowmeterList] = useState<DeviceList[]>([]);
  const [purityMonitoringInstrumentList, setPurityMonitoringInstrumentList] = useState<DeviceList[]>([]);
  const [id, setId] = useState<number>();
  const [type, setType] = useState<number>();
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (operationType === OperationType.EDIT) {
      const { selfId, type } = selectedNode.getData();
      setLoading(true);
      apiV2GasPreparationStationNitrogenGeneratorGetPost({ id: selfId })
        .then(res => {
          form.setFieldsValue({
            code: res?.code,
            name: res?.name,
            electricityMeterId: res?.electricityMeterId,
            flowmeterId: res?.flowmeterId,
            purityMonitoringInstrumentId: res?.purityMonitoringInstrumentId,
            id: res?.id,
          });

          setId(res?.id);
          setType(type);
        })
        .finally(() => setLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNode, operationType]);

  useEffect(() => {
    if ((operationType === OperationType.EDIT && id) || operationType === OperationType.ADD) {
      apiV2GasPreparationStationDeviceListPost({
        nodeId: id,
        nodeType: type as NodeType,
        uetId,
        stationId: stationData?.stationId,
        stationType: stationData?.stationType,
        deviceType: [DeviceType.ElectricityMeter, DeviceType.Flowmeter, DeviceType.PurityMonitoringInstrument],
      }).then(res => {
        setElectricityMeterList(
          res?.list?.find(item => item.deviceType === DeviceType.ElectricityMeter)?.deviceBasicDtoList ?? []
        );
        setFlowmeterList(res?.list?.find(item => item.deviceType === DeviceType.Flowmeter)?.deviceBasicDtoList ?? []);
        setPurityMonitoringInstrumentList(
          res?.list?.find(item => item.deviceType === DeviceType.PurityMonitoringInstrument)?.deviceBasicDtoList ?? []
        );
      });
    }
  }, [id, operationType, stationData, type, uetId]);

  const electricityMeterOptions = useMemo(() => {
    if (electricityMeterList?.length !== 0) {
      return electricityMeterList?.map(i => ({ label: i.name, value: i.id }));
    }
  }, [electricityMeterList]);
  const flowmeterOptions = useMemo(() => {
    if (flowmeterList?.length !== 0) {
      return flowmeterList?.map(i => ({ label: i.name, value: i.id }));
    }
  }, [flowmeterList]);

  const purityMonitoringInstrumentOptions = useMemo(() => {
    if (purityMonitoringInstrumentList?.length !== 0) {
      return purityMonitoringInstrumentList?.map(i => ({ label: i.name, value: i.id }));
    }
  }, [purityMonitoringInstrumentList]);

  const handleForm = () => {
    form.validateFields().then(() => {
      const values = {
        ...form.getFieldsValue(true),
        canvasId: selectedNode?.id,
        stationId: stationData?.stationId,
        stationCode: stationData?.stationCode,
      };

      setConfirmLoading(true);
      if (operationType === OperationType.ADD) {
        apiV2GasPreparationStationNitrogenGeneratorAddPost(values)
          .then(res => {
            message.success('新增成功');
            onOk({ ...values, selfId: res.id });
          })
          .finally(() => setConfirmLoading(false));
      }

      if (operationType === OperationType.EDIT) {
        apiV2GasPreparationStationNitrogenGeneratorEditPost(values)
          .then(res => {
            message.success('编辑成功');
            onOk({ ...values, selfId: res.id });
          })
          .finally(() => setConfirmLoading(false));
      }
    });
  };

  return (
    <Modal
      title="制氮机节点"
      contentClassName="modal-form-content"
      open
      bodyScroll
      onCancel={onCancel}
      onOk={handleForm}
      confirmLoading={confirmLoading}
    >
      <Spin spinning={loading}>
        <Form form={form}>
          {operationType === OperationType.EDIT && (
            <FormItem label="节点编号" name="code">
              <Input placeholder="请输入" disabled />
            </FormItem>
          )}
          <FormItem
            label="节点名称"
            name="name"
            rules={[
              { required: true, message: '请输入节点名称' },
              { min: 2, message: '最少输入2个字符' },
              { max: 10, message: '最多输入10个字符' },
            ]}
          >
            <Input placeholder="请输入节点名称" allowClear />
          </FormItem>
          <FormItem label="计量电表" name="electricityMeterId">
            <Select
              placeholder="请选择"
              options={electricityMeterOptions}
              showSearch
              allowClear
              optionFilterProp="label"
            />
          </FormItem>
          <FormItem label="流量计" name="flowmeterId">
            <Select placeholder="请选择" options={flowmeterOptions} showSearch allowClear optionFilterProp="label" />
          </FormItem>
          <FormItem label="纯度检测仪" name="purityMonitoringInstrumentId">
            <Select
              placeholder="请选择"
              options={purityMonitoringInstrumentOptions}
              showSearch
              allowClear
              optionFilterProp="label"
            />
          </FormItem>
        </Form>
      </Spin>
    </Modal>
  );
};

export default NitrogenGeneratorNode;
